import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { AppDispatch, GetState, RootState } from '../../store'

import { makeFolderObject, getSortedItems, getNewOrder } from './helpers'
import { uniqueArray } from '../../utilities'
import {
  getArticle,
  getArticles,
  putArticle,
  postArticle,
  deleteArticle as deleteArticleApi
} from '../../lib/api'
export { sortItems } from './helpers'

const SEARCH_DELAY = 500
const MIN_SEARCH_LENGTH = 3

type ArticleState = {
  loaded: boolean
  root: APIArticleRoot | null
  articles: Record<string, APIArticleGeneral>
  error: any
  successMessage: any
  loading: string[]
  selected: string | null
  editing: boolean
  creating: boolean

  // Searching state
  searchVal: string
  searched: APIArticleRoot | null
  searchTimer: NodeJS.Timeout | null
  searching: boolean

  // Searching/creating folders state
  folderSearchVal: string
  folderSearchResults: APIArticleItem[] | null
  folderSearchTimer: NodeJS.Timeout | null
  folderSearching: boolean
  newFolderName: string | null
  saving: boolean
  knownFolders: APIArticleRoot['items']
}

const initialState: ArticleState = {
  loaded: false,
  root: null,
  articles: {},
  error: null,
  successMessage: null,
  loading: [],
  selected: null,
  editing: false,
  creating: false,

  // Searching state
  searchVal: '',
  searched: null,
  searchTimer: null,
  searching: false,

  // Searching/creating folders state
  folderSearchVal: '',
  folderSearchResults: null,
  folderSearchTimer: null,
  folderSearching: false,
  newFolderName: null,
  saving: false,
  knownFolders: []
}

// Slice for the store

const articleSlice = createSlice({
  name: 'article',
  initialState,
  reducers: {
    resetArticle: () => {
      return initialState
    },

    setRoot: (state: ArticleState, action: PayloadAction<APIArticleRoot>) => {
      state.root = action.payload
      state.loaded = true
      state.knownFolders = action.payload.items.filter(
        item => item.type === 'folder'
      )
    },

    setArticle: (
      state: ArticleState,
      action: PayloadAction<APIArticleGeneral>
    ) => {
      state.articles[action.payload.id] = action.payload
    },

    setError: (state: ArticleState, action: PayloadAction<any>) => {
      state.error = action.payload
      state.loaded = true
    },

    clearError: (state: ArticleState) => {
      state.error = null
    },

    startLoading: (state: ArticleState, action: PayloadAction<string>) => {
      state.loading.push(action.payload)
    },

    selectArticle: (state: ArticleState, action: PayloadAction<string>) => {
      state.selected = action.payload
      state.editing = false
      state.searched = null
      state.searchVal = ''
    },

    setEditing: (state: ArticleState, action: PayloadAction<boolean>) => {
      state.editing = action.payload
      if (!action.payload) {
        state.newFolderName = null
      }
    },

    setCreating: (state: ArticleState, action: PayloadAction<boolean>) => {
      state.creating = action.payload
    },

    // Searching functions
    setSearchVal: (
      state: ArticleState,
      action: PayloadAction<{ val: string; timer: NodeJS.Timeout }>
    ) => {
      const { val, timer } = action.payload
      clearTimeout(state.searchTimer)
      state.searchTimer = timer
      state.searchVal = val
      if (val.length === 0) {
        state.searched = null
      }
    },

    startSearch: (state: ArticleState) => {
      state.searching = true
    },

    endSearch: (state: ArticleState, action: PayloadAction<APIArticleRoot>) => {
      state.searched = action.payload
      state.searching = false
    },

    // Folder searching functions
    setFolderSearchVal: (
      state: ArticleState,
      action: PayloadAction<{ val: string; timer: NodeJS.Timeout }>
    ) => {
      const { val, timer } = action.payload
      clearTimeout(state.folderSearchTimer)
      state.folderSearchTimer = timer
      state.folderSearchVal = val
      state.newFolderName = null
      if (val.length === 0) {
        state.folderSearchResults = null
      }
    },

    startFolderSearch: (state: ArticleState) => {
      state.folderSearching = true
    },

    endFolderSearch: (
      state: ArticleState,
      action: PayloadAction<APIArticleItem[]>
    ) => {
      state.folderSearchResults = action.payload
      state.folderSearching = false
    },

    createNewFolder: (state: ArticleState) => {
      state.newFolderName = state.folderSearchVal
    },

    unparentArticle: (state: ArticleState, action: PayloadAction<string>) => {
      const id = action.payload
      if (id in state.articles) {
        state.articles[id].items = state.articles[id].items.filter(
          item => item.id !== state.selected
        )
        state.articles[id].total_items = state.articles[id].items.length
      }
    },

    parentArticle: (
      state: ArticleState,
      action: PayloadAction<{ parent: string | null; article: APIArticleItem }>
    ) => {
      const { parent, article } = action.payload
      if (parent) {
        const oldIdx = state.articles[parent].items.findIndex(
          item => item.id === article.id
        )

        if (oldIdx >= 0) {
          state.articles[parent].items[oldIdx] = article
        } else {
          state.articles[parent].items.unshift(article)
        }

        // Update counts
        const newLength = state.articles[parent].items.length
        state.articles[parent].total_items = newLength
        const rootItem = state.root.items.find(item => item.id === parent)
        if (rootItem) {
          rootItem.total_items = newLength
        }
      } else {
        if (Array.isArray(state.root.items)) {
          const oldIdx = state.root.items.findIndex(
            item => item.id === article.id
          )

          if (oldIdx >= 0) {
            state.root.items[oldIdx] = article
          } else {
            state.root.items.unshift(article)
          }

          state.root.total_items = state.root.items.length
        }
      }
    },

    updateRootItemCounts: (
      state: ArticleState,
      action: PayloadAction<{ oldParent: string; newParent: string }>
    ) => {
      const { oldParent, newParent } = action.payload

      const oldIdx = state.root.items.findIndex(item => item.id === oldParent)
      if (oldIdx >= 0) {
        state.root.items[oldIdx].total_items -= 1
      }

      const newIdx = state.root.items.findIndex(item => item.id === newParent)
      if (newIdx >= 0) {
        state.root.items[newIdx].total_items += 1
      }
    },

    updateRootItemCount: (
      state: ArticleState,
      action: PayloadAction<{ parent: string }>
    ) => {
      const { parent } = action.payload

      if (parent) {
        const oldIdx = state.root.items.findIndex(item => item.id === parent)
        state.root.items[oldIdx].total_items += 1
      } else {
        state.root.total_items += 1
      }
    },

    resetFolderSearch: (state: ArticleState) => {
      state.folderSearchVal = ''
      state.folderSearchResults = null
      state.folderSearchTimer = null
      state.folderSearching = false
    },

    addFolderToRoot: (
      state: ArticleState,
      action: PayloadAction<APIArticleItem>
    ) => {
      state.root.total_items += 1
      const item = action.payload
      state.root.items.push(item)
      state.knownFolders.push(item)
    },

    setSaving: (state: ArticleState, action: PayloadAction<boolean>) => {
      state.saving = action.payload
    },

    removeArticle: (state: ArticleState, action: PayloadAction<string>) => {
      const id = action.payload

      // Remove from current search results
      if (state.searched?.items && Array.isArray(state.searched?.items)) {
        state.searched.items = state.searched.items.filter(
          item => item.id !== id
        )
        state.searched.total_items = state.searched.items.length
      }

      // Remove from root
      if (state.root?.items && Array.isArray(state.root?.items)) {
        state.root.items = state.root.items.filter(item => item.id !== id)
        state.root.total_items = state.root.items.length
      }

      // Remove from saved articles
      if (id in state.articles) {
        delete state.articles[id]
      }

      // Remove children from articles
      Object.values(state.articles).forEach(article => {
        const oldCount = article.items?.length ?? 0

        if (article?.items && Array.isArray(article?.items)) {
          article.items = article.items.filter(item => item.id !== id)
          article.total_items = article.items.length
        }

        // Need to update root item to match
        if (oldCount !== article.total_items) {
          const item = state.root.items.find(item => item.id === article.id)
          if (item) {
            item.total_items -= 1
          }
        }
      })
    },

    createFolder: (
      state: ArticleState,
      action: PayloadAction<APIArticleItem>
    ) => {
      // TODO: Enable this when we enable folder nesting
      // const { parent_id } = payload
      // if (parent_id) {
      //   if (parent_id in state.articles) {
      //     const parent = state.articles[parent_id]
      //     if (Array.isArray(parent.items)) {
      //       parent.items.push(payload)
      //     } else {
      //       parent.items = [payload]
      //     }
      //     parent.total_items = parent.items.length
      //   }
      // } else {
      state.root.items.unshift(action.payload)
      state.root.total_items = state.root.items.length
      // }
    },

    returnToRoot: (state: ArticleState) => {
      state.selected = null
      state.editing = false
      state.searched = null
      state.searchVal = ''
    },

    clearSuccess: (state: ArticleState) => {
      state.successMessage = null
    },

    setSuccess: (state: ArticleState, action: PayloadAction<any>) => {
      state.successMessage = action.payload
    }
  }
})

// Dispatch-able functions here

export const {
  setRoot,
  startLoading,
  setArticle,
  setEditing,
  setCreating,
  resetFolderSearch,
  createNewFolder,
  returnToRoot
} = articleSlice.actions

// Private actions that should only be dispatched in this file's thunks
const {
  setError,
  clearError,
  selectArticle: selectAction,
  setSearchVal: setSearchValAction,
  startSearch,
  endSearch,
  startFolderSearch,
  endFolderSearch,
  setFolderSearchVal: setFolderSearchValAction,
  addFolderToRoot,
  unparentArticle,
  parentArticle,
  updateRootItemCounts,
  updateRootItemCount,
  setSaving,
  removeArticle,
  createFolder,
  clearSuccess,
  setSuccess
} = articleSlice.actions

// Thunk-like actions here

export const fetchArticles = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const articles = await getArticles()
      dispatch(setRoot(articles))
    } catch (err) {
      dispatch(setError(err))
    }
  }
}

export const fetchArticle = (id: string) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const { articles, loading } = selectArticles(getState())
    if (id in articles || loading.includes(id)) {
      return
    }

    try {
      dispatch(startLoading(id))
      const article = await getArticle(id)
      dispatch(setArticle(article))
    } catch (err) {
      dispatch(setError(err))
    }
  }
}

export const selectArticle = (id: string | null) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const { articles, loading, creating } = selectArticles(getState())

    if (creating) {
      dispatch(setCreating(false))
    }

    if (id in articles || id === null) {
      dispatch(selectAction(id))
      return
    }

    if (loading.includes(id)) {
      return
    }

    await dispatch(fetchArticle(id))
    dispatch(selectAction(id))
  }
}

export const setSearchVal = (val: string) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const timer = setTimeout(async () => {
      const { searchVal } = selectArticles(getState())

      if (searchVal.length < MIN_SEARCH_LENGTH) {
        return
      }

      dispatch(startSearch())
      const searchResults = await getArticles(searchVal)
      dispatch(endSearch(searchResults))
    }, SEARCH_DELAY)

    dispatch(setSearchValAction({ val, timer }))
  }
}

export const setFolderSearchVal = (val: string) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const timer = setTimeout(async () => {
      const { folderSearchVal } = selectArticles(getState())

      if (folderSearchVal.length < MIN_SEARCH_LENGTH) {
        return
      }

      dispatch(startFolderSearch())
      const searchResults = await getArticles(folderSearchVal)
      const folders = searchResults.items.filter(item => item.type === 'folder')
      dispatch(endFolderSearch(folders))
    }, SEARCH_DELAY)

    dispatch(setFolderSearchValAction({ val, timer }))
  }
}

export const saveArticle = (id: string, newVal: APIArticleUpdate) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const { articles, newFolderName, creating } = selectArticles(getState())
    dispatch(setSaving(true))

    const prevArticle = articles[id] ?? { tags: null, parent: null }
    const combined: APIArticleUpdate = {
      ...prevArticle,
      ...newVal,
      tags: uniqueArray([...(prevArticle.tags ?? []), ...(newVal.tags ?? [])])
    }

    let folderChanged = prevArticle.parent?.id !== newVal.parent_id

    const toSave: APIArticleUpdate = {
      type: creating ? 'article' : combined.type,
      parent_id: combined.parent_id ?? null,
      title: combined.title ?? '',
      subtitle: combined.subtitle || null,
      body: combined.body || null,
      published_at: combined.published_at,
      archived_at: combined.archived_at,
      media_ids: combined.media_ids,
      tags: combined.tags,
      order: combined.order ?? 0
    }

    // Add order if needed
    if (!combined.hasOwnProperty('order')) {
      const sorted = getSortedItems(getState, toSave.parent_id)
      if (sorted.length) {
        const newOrder = getNewOrder(sorted, sorted[0].id, false)
        toSave.order = newOrder
      }
    }

    try {
      // Create new folder before save
      if (newFolderName) {
        const newFolderData = makeFolderObject(newFolderName)
        const newFolder = await postArticle(newFolderData)
        newFolder.total_items = newFolder.total_items ?? 0
        newFolder.items = newFolder.items ?? []

        // Update root and stored articles with new folder
        dispatch(addFolderToRoot(newFolder))
        dispatch(setArticle(newFolder))

        // Update new article
        toSave.parent_id = newFolder.id
        folderChanged = true
      }

      // Create or update article

      if (creating) {
        // Create article
        const newArticle = await postArticle(toSave)
        newArticle.updated_at = new Date().toISOString()
        dispatch(setArticle(newArticle))

        // Update this article in the parents items list
        dispatch(
          parentArticle({ parent: toSave.parent_id, article: newArticle })
        )
        dispatch(updateRootItemCount({ parent: toSave.parent_id }))

        dispatch(setCreating(false))
        dispatch(setSaving(false))
        dispatch(selectArticle(newArticle.id))
      } else {
        // Update article
        const newArticle = await putArticle(id, toSave)
        dispatch(setArticle(newArticle))

        if (folderChanged) {
          const oldParent = prevArticle.parent?.id
          const newParent = toSave.parent_id

          // Remove this article from the old parents items
          if (oldParent) {
            dispatch(unparentArticle(oldParent))
          }

          // Add this article to the new parents items
          dispatch(parentArticle({ parent: newParent, article: newArticle }))

          // Update counts on the root
          dispatch(updateRootItemCounts({ oldParent, newParent }))
        } else {
          // Update this article in the parents items list
          dispatch(
            parentArticle({ parent: toSave.parent_id, article: newArticle })
          )
        }

        dispatch(setEditing(false))
        dispatch(setSaving(false))
      }
    } catch (err) {
      dispatch(setError(err))
      dispatch(setSaving(false))
    }
  }
}

export const deleteArticle = ({
  id,
  redirect,
  type = 'article'
}: {
  id: string
  redirect?: string
  type?: APIArticle['type']
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(clearError())
      dispatch(clearSuccess())

      await deleteArticleApi(id)
      if (redirect !== undefined) {
        dispatch(selectArticle(redirect))
      }
      dispatch(removeArticle(id))

      dispatch(
        setSuccess(
          `Your ${type === 'article' ? 'document' : type} has been deleted`
        )
      )
      setTimeout(() => dispatch(clearSuccess()), 2700)
    } catch (err) {
      dispatch(setError(err))
    }
  }
}

export const addFolder = (name: string, parent: string, addBelow: string) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    try {
      dispatch(clearError())
      const newFolderData = makeFolderObject(name)

      // Figure out order of the new folder
      // TODO: Use real parent when nesting is supported
      const sorted = getSortedItems(getState, null)
      if (sorted.length) {
        let newOrder = 0
        if (addBelow) {
          // Insert after folder
          newOrder = getNewOrder(sorted, addBelow, true)
        } else {
          // Insert at top
          newOrder = getNewOrder(sorted, sorted[0].id, false)
        }
        newFolderData.order = newOrder
      }

      // TODO: Add this when api supports folder nesting
      // if (parent) {
      //   newFolderData.parent_id = parent
      // }

      const newFolder = await postArticle(newFolderData)
      newFolder.total_items = newFolder.total_items ?? 0
      newFolder.items = newFolder.items ?? []
      dispatch(createFolder(newFolder))
      dispatch(selectArticle(newFolder.id))
    } catch (err) {
      dispatch(setError(err))
    }
  }
}

export const renameArticle = ({
  article,
  name
}: {
  article: APIArticleGeneral
  name: string
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(clearError())
      dispatch(clearSuccess())

      // Create edited article object
      const {
        id,
        type,
        parent,
        subtitle,
        body,
        published_at,
        archived_at,
        media_ids,
        tags,
        order = 0
      } = article
      const toSave: APIArticleUpdate = {
        type,
        parent_id: parent?.id ?? null,
        title: name,
        subtitle,
        body: body ?? null,
        published_at,
        archived_at,
        media_ids: media_ids,
        tags: tags,
        order
      }

      // Update article
      const newArticle = await putArticle(id, toSave)

      // Api blanks these so just use the stored values
      newArticle.total_items = article.total_items ?? 0
      newArticle.items = article.items ?? []

      // Update this article in the parents items list
      dispatch(parentArticle({ parent: toSave.parent_id, article: newArticle }))
      dispatch(setArticle(newArticle))

      dispatch(setSuccess('Folder changes saved successfully'))
      setTimeout(() => dispatch(clearSuccess()), 2700)
    } catch (err) {
      dispatch(setError(err))
    }
  }
}

export const togglePublish = (article: APIArticleGeneral) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(clearError())
      dispatch(clearSuccess())

      // Create edited article object
      const {
        id,
        type,
        parent,
        title,
        subtitle,
        body,
        published_at,
        archived_at,
        media_ids,
        tags,
        order
      } = article
      const toSave: APIArticleUpdate = {
        type,
        parent_id: parent?.id ?? null,
        title,
        subtitle,
        body: body ?? null,
        published_at: published_at ? null : new Date().toISOString(),
        archived_at,
        media_ids: media_ids,
        tags: tags,
        order
      }

      // Update article
      const newArticle = await putArticle(id, toSave)

      // Api blanks these so just use the stored values
      newArticle.total_items = article.total_items ?? 0
      newArticle.items = article.items ?? []

      // Update this article in the parents items list
      dispatch(
        parentArticle({
          parent: type === 'folder' ? toSave.parent_id : newArticle.parent?.id,
          article: newArticle
        })
      )
      dispatch(setArticle(newArticle))

      dispatch(
        setSuccess(
          `${
            type === 'folder' ? 'Folder' : 'Article'
          } changes saved successfully`
        )
      )
      setTimeout(() => dispatch(clearSuccess()), 2700)
    } catch (err) {
      dispatch(setError(err))
    }
  }
}

export const moveArticle = (
  source: string,
  target: string,
  sourceParent: string | null,
  targetParent: string | null,
  after: boolean
) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    try {
      const { articles, selected } = selectArticles(getState())

      const targetSorted = getSortedItems(getState, targetParent)
      const sourceSorted = getSortedItems(getState, sourceParent)

      const existingItem = sourceSorted.find(item => item.id === source)
      let sourceArticle: APIArticleGeneral = {
        ...existingItem,
        type: existingItem.type as 'article' | 'folder',
        parent: null,
        body: '',
        items: []
      }

      // Figure out order of this moved article
      const newOrder = getNewOrder(targetSorted, target, after)
      const sameFolder = sourceParent === targetParent

      if (sameFolder && sourceArticle.order === newOrder) {
        return
      }

      // Get source article from storage or api
      if (!articles[source]) {
        dispatch(startLoading(source))
        sourceArticle = await getArticle(source)
        dispatch(setArticle(sourceArticle))
      } else {
        sourceArticle = articles[source]
      }

      // Format updated article
      const {
        type,
        title,
        subtitle,
        body,
        published_at,
        archived_at,
        media_ids,
        tags
      } = sourceArticle
      const toSave: APIArticleUpdate = {
        type,
        parent_id: targetParent,
        title,
        subtitle,
        body: body ?? null,
        published_at,
        archived_at,
        media_ids,
        tags,
        order: newOrder
      }

      // Does the move of the article
      const performArticleMove = (
        article: APIArticleUpdate & { id: string } & Pick<
            APIArticleGeneral,
            'total_items' | 'items'
          >
      ) => {
        // Update this article in the parents items list
        const finishMove = () => {
          dispatch(
            parentArticle({
              parent: targetParent,
              article: article as unknown as APIArticleItem
            })
          )
          dispatch(setArticle(article as unknown as APIArticleGeneral))
        }

        // Move within the same folder
        if (sameFolder) {
          finishMove()
          return
        }

        // Move when not looking at the article
        if (selected !== source) {
          dispatch(removeArticle(source))
          finishMove()
          return
        }

        // Move between folders
        dispatch(selectArticle(null))
        dispatch(removeArticle(source))
        finishMove()
        dispatch(selectArticle(source))
      }

      // Quick mock move
      performArticleMove({
        ...toSave,
        id: source,
        total_items: sourceArticle.total_items ?? 0,
        items: sourceArticle.items ?? []
      })

      // Update article
      const newArticle = await putArticle(source, toSave)

      // Api blanks these so just use the stored values
      newArticle.total_items = sourceArticle.total_items ?? 0
      newArticle.items = sourceArticle.items ?? []

      // Update this article in the lists
      dispatch(
        parentArticle({
          parent: targetParent,
          article: newArticle
        })
      )
      dispatch(setArticle(newArticle))
    } catch (err) {
      dispatch(setError(err))
    }
  }
}

export const startCreateInFolder = (folder: string) => {
  return async (dispatch: AppDispatch) => {
    dispatch(selectArticle(folder))

    // Force creating flag for the next 2s, since select can cause creating to be unset
    let count = 0
    const timer = setInterval(() => {
      dispatch(setCreating(true))
      count++
      if (count === 9) {
        clearInterval(timer)
      }
    }, 200)
  }
}

export const selectArticles = (state: RootState) => state.article

export default articleSlice.reducer
